import React, {
  ChangeEvent,
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  ButtonGroup,
  Grid,
  InputAdornment,
  Stack,
  Tooltip,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import SpacedTextInput, {
  SpacedAutocompleteInput,
  SpacedDropDownSelect,
} from "components/common/ui/SpacedTextInput";
import CircleIconButton, {
  CircleIconButtonProps,
} from "components/common/ui/CircleIconButton";
import MenuViewBox from "components/menu/ui/common/MenuViewBox";
import { Menu } from "client/jspPlatformExperiment";
import { useParams } from "react-router-dom";
import { DisplayMenuLoop } from "models/menu/menuLoop";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import editingMenuLoopSelector from "store/selectors/menu/menuLoopDetailSelector";
import menuBatchOperationSelector from "store/selectors/menu/menuBatchOperationSelector";
import CommonButton from "components/common/ui/CommonButton";
import Typography from "@mui/material/Typography";
import styled, { css } from "styled-components";
import dayjs from "dayjs";
import { daysPerWeek } from "models/common/constants/time";
import menuOptionsSelector from "store/selectors/menu/menuOptionsSelector";
import { getMenuOptionMaps } from "utils/menu/menuOptionDisplayUtils";
import { getLoopDayGridOffset } from "utils/menu/menuLoop/menuLoopDayGridUtils";
import PillButton from "components/common/ui/PillButton";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import { menuLoopActions } from "store/slices/menu/menuLoopDetailSlice";
import { menuOptionsActions } from "store/slices/menu/menuOptionsSlice";
import { menuBatchOperationActions } from "store/slices/menu/menuBatchOperationSlice";
import CreateMenuInLoopDialog from "components/menu/ui/menuLoop/CreateMenuInLoopDialog";
import {
  getMenusUpdateDeepInBatchFromMenus,
  updateSectionCapValuesInBatch,
} from "utils/menu/menuLoop/menuLoopDisplayToMenuDeepUpdateBatch";
import ColoredCircularProgress from "components/common/ui/ColorCircularProgress";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import {
  getMenuSectionNameOptions,
  MenuItemIndexOptions,
} from "models/menu/constants/sectionName";
import {
  getMealLabelByServingTime,
  MEAL_SERVING_TIME_OPTIONS,
} from "models/menu/constants/servingTimes";

interface SideLabelProps {
  sizepx?: number;
}

const SideLabel = styled(Box)<SideLabelProps>`
  height: ${(props) => props.sizepx ?? 40}px;
  width: ${(props) => props.sizepx ?? 40}px;
  align-content: center;
  text-align-last: center;
  vertical-align: bottom;
  display: inline-block;
`;

const ALaCartePillButton = styled(PillButton)`
  margin: 0;
  min-width: 200px;
`;

const MealTypeSwitchButton = styled(CommonButton)`
  margin: 1em 0;

  &.Mui-disabled {
    color: rgb(200, 200, 200);
    background-color: rgb(244, 244, 244);
    border: 2px solid rgb(235, 235, 235);
  }
`;

const MealTypeSwitchButtonGroup = styled(ButtonGroup)`
  margin: 3em 0 2em;
  border: 0;
`;

const MenuInLoopActionBox = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 1.5em;
  margin-bottom: -18px;
`;

const MEAL_SEGMENT_SET_COLOR = "rgb(147, 196, 125)";
const MEAL_SEGMENT_UNSET_COLOR = "rgb(244, 244, 244)";
/** Colour of the dot marking the loop day served on the loop's start date */
const START_DAY_DOT_COLOR = "rgb(255, 179, 0)";

/** Side length of a day cell and of the W/weekday side labels */
const DAY_CELL_SIZE_PX = 40;
/** Vertical distance between rows: cell size plus the MUI Grid spacing={1} gap */
const DAY_ROW_PITCH_PX = DAY_CELL_SIZE_PX + 8;
const DAY_MOVE_DURATION_MS = 300;

const getDayCellRow = ({
  dayIdxInLoop,
  gridOffset,
}: {
  dayIdxInLoop: number;
  gridOffset: number;
}): number => Math.floor((dayIdxInLoop - 1 + gridOffset) / daysPerWeek);

/** Pixel position of a day circle on the grid canvas; column 0 is the W label */
const getDayCellPosition = ({
  dayIdxInLoop,
  gridOffset,
}: {
  dayIdxInLoop: number;
  gridOffset: number;
}): { x: number; y: number } => {
  const cellIdx = dayIdxInLoop - 1 + gridOffset;
  const columnIdx = (cellIdx % daysPerWeek) + 1;
  const weekRowIdx = getDayCellRow({ dayIdxInLoop, gridOffset });
  return {
    x: columnIdx * DAY_CELL_SIZE_PX,
    y: weekRowIdx * DAY_ROW_PITCH_PX,
  };
};

const toTranslate = ({ x, y }: { x: number; y: number }): string =>
  `translate(${x}px, ${y}px)`;

const getDayCellTransform = (args: {
  dayIdxInLoop: number;
  gridOffset: number;
}): string => toTranslate(getDayCellPosition(args));

/** Fixed-size canvas the week labels and day circles are positioned on */
const DayGridCanvas = styled(Box)`
  position: relative;
  width: ${DAY_CELL_SIZE_PX * (daysPerWeek + 1)}px;
  transition: height 300ms ease;
`;

const WeekLabelCell = styled(SideLabel)`
  position: absolute;
  left: 0;
`;

/**
 * One day circle, placed by transform so a change of the grid offset slides
 * it to its new cell instead of re-mounting it there
 */
const DayCell = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: ${DAY_CELL_SIZE_PX}px;
  height: ${DAY_CELL_SIZE_PX}px;
  display: inline-block;
  transition: transform ${DAY_MOVE_DURATION_MS}ms ease;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const DayCellMarkerWrapper = styled.span`
  position: relative;
  display: inline-block;
  vertical-align: bottom;
`;

const StartDayDot = styled.span`
  position: absolute;
  top: 1px;
  right: 1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${START_DAY_DOT_COLOR};
  border: 2px solid white;
  z-index: 1;
  pointer-events: none;
`;

const StartDayLabel = styled(Typography)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgb(120, 120, 120);
  padding: 6px 8px;
`;

const StartDayLabelDot = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${START_DAY_DOT_COLOR};
  flex-shrink: 0;
`;

interface DayIndexCircleButtonProps extends CircleIconButtonProps {
  /** Breakfast/lunch/dinner set flags, painted top/middle/bottom */
  mealsegments?: boolean[];
}

const DayIndexCircleButton = styled(CircleIconButton).withConfig({
  shouldForwardProp: (prop) => prop !== "mealsegments",
})<DayIndexCircleButtonProps>`
  ${({ mealsegments, positive }) => {
    if (positive || !mealsegments?.some(Boolean)) {
      return css``;
    }
    const [breakfastSet, lunchSet, dinnerSet] = mealsegments;
    const segmentColor = (isSet: boolean | undefined): string =>
      isSet ? MEAL_SEGMENT_SET_COLOR : MEAL_SEGMENT_UNSET_COLOR;
    return css`
      background: linear-gradient(
        to bottom,
        ${segmentColor(breakfastSet)} 0% 20%,
        ${segmentColor(lunchSet)} 20% 80%,
        ${segmentColor(dinnerSet)} 80% 100%
      );
      /* Size the gradient to the border-box so the band under the
         transparent border is not filled by the repeated tile */
      background-origin: border-box;
      border-color: transparent;
      color: ${lunchSet ? MEAL_SEGMENT_UNSET_COLOR : MEAL_SEGMENT_SET_COLOR};
    `;
  }}
`;

interface MenusInLoopEditSectionProps {}

const MenusInLoopEditSection: FC<MenusInLoopEditSectionProps> = () => {
  const dispatch = useAppDispatch();
  const promptSnackBar = usePromptSnackBar();
  const { menuLoopId } = useParams();
  const editingMenuLoop = useAppSelector(
    editingMenuLoopSelector.editingMenuLoop,
  );
  const isSavingMenuLoop = useAppSelector(editingMenuLoopSelector.isSaving);
  const menuOptions = useAppSelector(menuOptionsSelector.menuOptions);
  const idToMenu = useAppSelector(menuOptionsSelector.idToMenuOptionRecords);
  const menuOptionsIsLoading = useAppSelector(menuOptionsSelector.isLoading);
  const isBatchUpdatingMenus = useAppSelector(
    menuBatchOperationSelector.isSaving,
  );

  const [offset, setOffset] = useState<number>(
    getLoopDayGridOffset({
      startDate: editingMenuLoop?.start_date,
      startDayIndex: editingMenuLoop?.start_day_index,
    }),
  );
  const [focusDayIdx, setFocusDayIdx] = useState(1);
  const [daysPerCycle, setDaysPerCycle] = useState<number | null>(
    editingMenuLoop?.days_per_cycle ?? null,
  );
  const [displayMenus, setDisplayMenus] = useState<Menu[]>([]);
  const [displayWeekNumber, setDisplayWeekNumber] = useState(4);
  const [chosenServingTime, setChosenServingTime] = useState("12:00:00");
  const [menuOptionToIdMap, setMenuOptionToIdMap] = useState<
    Record<string, number>
  >({ "": 0 });
  const [selectedMenuId, setSelectedMenuId] = useState(0);
  const [batchUpdatingSectionName, setBatchUpdatingSectionName] =
    useState<string>("");
  const [batchUpdatingProductionNumber, setBatchUpdatingProductionNumber] =
    useState<number | null>(null);
  const [batchUpdatingItemIndex, setBatchUpdatingItemIndex] = useState<
    number | null
  >(null);
  const [
    batchUpdatingPublishPriorToServingTime,
    setBatchUpdatingPublishPriorToServingTime,
  ] = useState<number | null>(null);
  const [isUpdatingProductionNumbers, setIsUpdatingProductionNumbers] =
    useState<boolean>(false);
  const [
    isUpdatingPublishPriorToServingTime,
    setIsUpdatingPublishPriorToServingTime,
  ] = useState<boolean>(false);
  const [isCreateMenuDialogOpen, setIsCreateMenuDialogOpen] =
    useState<boolean>(false);
  const [editingMenuId, setEditingMenuId] = useState<number | null>(null);
  /**
   * Use effect to sync the menu option names
   */
  useEffect(() => {
    const { menuOptionNamesToIdsMap } = getMenuOptionMaps(menuOptions);
    setMenuOptionToIdMap(menuOptionNamesToIdsMap);
  }, [menuOptions]);

  useEffect(() => {
    setDisplayWeekNumber(
      Math.ceil(((daysPerCycle ?? 0) + offset) / daysPerWeek),
    );
  }, [daysPerCycle, offset]);

  useEffect(() => {
    if (editingMenuLoop) {
      setOffset(
        getLoopDayGridOffset({
          startDate: editingMenuLoop.start_date,
          startDayIndex: editingMenuLoop.start_day_index,
        }),
      );
      setDaysPerCycle(editingMenuLoop?.days_per_cycle ?? null);
      if (editingMenuLoop.menu_links) {
        setDisplayMenus(
          editingMenuLoop.menu_links
            .filter(
              (menuInLoop) =>
                menuInLoop.looping_day_index === focusDayIdx &&
                menuInLoop.serving_time === chosenServingTime,
            )
            .map((menuInLoop) => menuInLoop.menu),
        );
      }
    }
  }, [chosenServingTime, editingMenuLoop, focusDayIdx]);

  const handleSelectedMenuChange = useCallback(
    (_: SyntheticEvent, menuOption: string | null) => {
      if (menuOption) {
        setSelectedMenuId(menuOptionToIdMap[menuOption] ?? 0);
      } else {
        setSelectedMenuId(0);
      }
    },
    [menuOptionToIdMap],
  );

  const startDayIndex = editingMenuLoop?.start_day_index ?? 1;

  const dayCellElements = useRef(new Map<number, HTMLSpanElement>());
  const previousOffset = useRef(offset);

  /**
   * Days that move to another row when the offset changes have no straight
   * path to slide along. They keep moving sideways at the same speed as the
   * other circles while fading out, jump rows at the midpoint, and finish the
   * same sideways travel while fading in. Days staying on their row keep the
   * CSS transform transition.
   */
  useLayoutEffect(() => {
    const fromOffset = previousOffset.current;
    previousOffset.current = offset;
    if (fromOffset === offset) {
      return;
    }
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    dayCellElements.current.forEach((element, dayIdxInLoop) => {
      const changesRow =
        getDayCellRow({ dayIdxInLoop, gridOffset: fromOffset }) !==
        getDayCellRow({ dayIdxInLoop, gridOffset: offset });
      if (
        !changesRow ||
        reduceMotion ||
        typeof element.animate !== "function"
      ) {
        return;
      }
      const from = getDayCellPosition({ dayIdxInLoop, gridOffset: fromOffset });
      const to = getDayCellPosition({ dayIdxInLoop, gridOffset: offset });
      // Sideways distance every non-wrapping circle travels; half of it is
      // covered on each side of the row jump so the speed matches
      const halfShiftX = ((offset - fromOffset) * DAY_CELL_SIZE_PX) / 2;
      // eslint-disable-next-line no-param-reassign
      element.style.transition = "none";
      const animation = element.animate(
        [
          { opacity: 1, transform: toTranslate(from) },
          {
            opacity: 0,
            transform: toTranslate({ x: from.x + halfShiftX, y: from.y }),
            offset: 0.5,
          },
          {
            opacity: 0,
            transform: toTranslate({ x: to.x - halfShiftX, y: to.y }),
            offset: 0.5,
          },
          { opacity: 1, transform: toTranslate(to) },
        ],
        { duration: DAY_MOVE_DURATION_MS, easing: "ease" },
      );
      animation.onfinish = () => {
        // eslint-disable-next-line no-param-reassign
        element.style.transition = "";
      };
    });
  }, [offset]);

  const buildDayIndexCell = useCallback(
    (dayIdxInLoop: number) => {
      const menuLinksOnDayIdx = editingMenuLoop?.menu_links.filter(
        (menuInLoop) => menuInLoop.looping_day_index === dayIdxInLoop,
      );
      const mealSegments = MEAL_SERVING_TIME_OPTIONS.map(
        ({ servingTime }) =>
          !!menuLinksOnDayIdx?.some(
            (menuInLoop) => menuInLoop.serving_time === servingTime,
          ),
      );
      const isStartDay = dayIdxInLoop === startDayIndex;
      const circle = (
        <DayIndexCircleButton
          sizepx={DAY_CELL_SIZE_PX}
          onClick={() => {
            setFocusDayIdx(dayIdxInLoop);
          }}
          positive={focusDayIdx === dayIdxInLoop}
          mealsegments={mealSegments}
        >
          <Typography variant="button">{dayIdxInLoop}</Typography>
        </DayIndexCircleButton>
      );
      return (
        <DayCell
          key={dayIdxInLoop}
          ref={(element) => {
            if (element) {
              dayCellElements.current.set(dayIdxInLoop, element);
            } else {
              dayCellElements.current.delete(dayIdxInLoop);
            }
          }}
          style={{
            transform: getDayCellTransform({
              dayIdxInLoop,
              gridOffset: offset,
            }),
          }}
        >
          {isStartDay ? (
            <DayCellMarkerWrapper>
              {circle}
              <StartDayDot />
            </DayCellMarkerWrapper>
          ) : (
            circle
          )}
        </DayCell>
      );
    },
    [editingMenuLoop?.menu_links, focusDayIdx, offset, startDayIndex],
  );

  /**
   * Commit a linkage change locally and, when editing an existing loop,
   * auto-save it to the server (create mode stays manual-save)
   */
  const commitMenuLoopLinks = useCallback(
    (nextMenuLoop: DisplayMenuLoop) => {
      dispatch(menuLoopActions.setMenuLoopDetail(nextMenuLoop));
      if (Number(menuLoopId)) {
        dispatch(
          menuLoopActions.updateMenuLoop({
            menuLoopId: Number(menuLoopId),
            menuLoop: nextMenuLoop,
          }),
        );
      }
    },
    [dispatch, menuLoopId],
  );

  const handleAddMenuClick = useCallback(() => {
    if (editingMenuLoop && selectedMenuId) {
      commitMenuLoopLinks({
        ...editingMenuLoop,
        menu_links: [
          ...editingMenuLoop.menu_links,
          {
            serving_time: chosenServingTime,
            looping_day_index: focusDayIdx,
            menu: idToMenu[`${selectedMenuId}`],
          },
        ],
      });
    }
  }, [
    chosenServingTime,
    commitMenuLoopLinks,
    editingMenuLoop,
    focusDayIdx,
    idToMenu,
    selectedMenuId,
  ]);

  const handleRemoveMenuClick = useCallback(
    (loopingDayIdx: number, menuId: number, servingTime: string) => () => {
      if (editingMenuLoop) {
        commitMenuLoopLinks({
          ...editingMenuLoop,
          menu_links: editingMenuLoop.menu_links.filter(
            (menuInLoop) =>
              !(
                menuInLoop.looping_day_index === loopingDayIdx &&
                menuInLoop.serving_time === servingTime &&
                menuInLoop.menu.id === menuId
              ),
          ),
        });
      }
    },
    [commitMenuLoopLinks, editingMenuLoop],
  );

  const generatedMenuName = useMemo(() => {
    const loopName = editingMenuLoop?.name ?? "";
    if (!loopName) {
      return loopName;
    }
    const serviceName = getMealLabelByServingTime(chosenServingTime);
    if (focusDayIdx <= 0) {
      return `${loopName} à la carte ${serviceName}`;
    }
    const startDate = editingMenuLoop?.start_date;
    if (!startDate || !dayjs(startDate).isValid()) {
      return `${loopName} ${serviceName}`;
    }
    const dateForDay = dayjs(startDate).add(focusDayIdx - startDayIndex, "day");
    const weekNumber = Math.floor((focusDayIdx + offset - 1) / daysPerWeek) + 1;
    return `${loopName} Week ${weekNumber} ${dateForDay.format(
      "dddd",
    )} ${serviceName}`;
  }, [
    chosenServingTime,
    editingMenuLoop?.name,
    editingMenuLoop?.start_date,
    focusDayIdx,
    offset,
    startDayIndex,
  ]);

  const handleSetAsStartDay = useCallback(() => {
    if (editingMenuLoop && focusDayIdx > 0) {
      dispatch(
        menuLoopActions.setMenuLoopDetail({
          ...editingMenuLoop,
          start_day_index: focusDayIdx,
        }),
      );
    }
  }, [dispatch, editingMenuLoop, focusDayIdx]);

  const handleOpenCreateMenuDialog = useCallback(() => {
    setEditingMenuId(null);
    setIsCreateMenuDialogOpen(true);
  }, []);

  const handleOpenEditMenuDialog = useCallback((menuId: number) => {
    setEditingMenuId(menuId);
    setIsCreateMenuDialogOpen(true);
  }, []);

  const handleCloseCreateMenuDialog = useCallback(() => {
    setIsCreateMenuDialogOpen(false);
    setEditingMenuId(null);
  }, []);

  const handleMenuSavedInLoop = useCallback(
    (savedMenu: Menu) => {
      if (editingMenuLoop) {
        dispatch(menuOptionsActions.setMenuOptions([savedMenu]));
        const existsInLinks = editingMenuLoop.menu_links.some(
          (link) => link.menu.id === savedMenu.id,
        );
        if (existsInLinks) {
          // Edited an already-linked menu: link structure unchanged, no auto-save
          dispatch(
            menuLoopActions.setMenuLoopDetail({
              ...editingMenuLoop,
              menu_links: editingMenuLoop.menu_links.map((link) =>
                link.menu.id === savedMenu.id
                  ? { ...link, menu: savedMenu }
                  : link,
              ),
            }),
          );
        } else {
          commitMenuLoopLinks({
            ...editingMenuLoop,
            menu_links: [
              ...editingMenuLoop.menu_links,
              {
                serving_time: chosenServingTime,
                looping_day_index: focusDayIdx,
                menu: savedMenu,
              },
            ],
          });
        }
      }
      setIsCreateMenuDialogOpen(false);
      setEditingMenuId(null);
    },
    [
      chosenServingTime,
      commitMenuLoopLinks,
      dispatch,
      editingMenuLoop,
      focusDayIdx,
    ],
  );

  const handleBatchUpdateProductionNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value === "") {
        setBatchUpdatingProductionNumber(null);
      } else {
        const intValue = parseInt(value, 10);
        if (!Number.isNaN(intValue) && intValue >= 0) {
          setBatchUpdatingProductionNumber(intValue);
        }
      }
    },
    [],
  );

  const handleBatchUpdatePublishPriorToServingTimeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value === "") {
        setBatchUpdatingPublishPriorToServingTime(null);
      } else {
        const intValue = parseInt(value, 10);
        if (!Number.isNaN(intValue) && intValue > 0) {
          setBatchUpdatingPublishPriorToServingTime(intValue);
        }
      }
    },
    [],
  );

  const handleBatchUpdateSectionNameChange = useCallback(
    (event: SelectChangeEvent) => {
      setBatchUpdatingSectionName(event.target.value);
    },
    [],
  );

  /**
   * Batch update production numbers for all menus in the loop
   */
  const handleBatchUpdateProductionNumbers = useCallback(() => {
    if (
      !editingMenuLoop ||
      !batchUpdatingSectionName ||
      batchUpdatingProductionNumber === null ||
      batchUpdatingProductionNumber === undefined ||
      !Number.isInteger(batchUpdatingProductionNumber) ||
      batchUpdatingProductionNumber < 0
    ) {
      return;
    }

    const newCap = batchUpdatingProductionNumber;

    // Get all menus from menu_links filtered by serving time
    const menus = editingMenuLoop.menu_links
      .filter((menuLink) => menuLink.serving_time === chosenServingTime)
      .map((menuLink) => menuLink.menu);

    // Convert menus to MenuUpdateDeepInBatch format
    const menusUpdateDeepInBatch = getMenusUpdateDeepInBatchFromMenus(menus);

    // Update cap values in the batch format
    const updatedMenusUpdateDeepInBatch = menusUpdateDeepInBatch.map(
      (menuUpdate) => {
        const updatedSections = menuUpdate.sections?.map((section) => {
          // Only process MenuOptionGroupUpdateDeepInBatch (has id)
          if ("id" in section && section.name === batchUpdatingSectionName) {
            return updateSectionCapValuesInBatch(
              section,
              batchUpdatingSectionName,
              newCap,
              !batchUpdatingItemIndex ? null : batchUpdatingItemIndex - 1,
            );
          }
          return section;
        });

        return {
          ...menuUpdate,
          sections: updatedSections,
        };
      },
    );

    // Dispatch batch update
    setIsUpdatingProductionNumbers(true);
    dispatch(
      menuBatchOperationActions.batchUpdateMenus(updatedMenusUpdateDeepInBatch),
    )
      .unwrap()
      .then((updatedMenus) => {
        setIsUpdatingProductionNumbers(false);
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Successfully update ${updatedMenus.length} menu(s) in loop`,
          severity: "success",
          autoHeightDuration: 5000,
        });
      })
      .catch((error) => {
        setIsUpdatingProductionNumbers(false);
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: typeof error === "string" ? error : "Failed to update menus",
          severity: "error",
        });
      });
  }, [
    editingMenuLoop,
    batchUpdatingSectionName,
    batchUpdatingProductionNumber,
    dispatch,
    chosenServingTime,
    batchUpdatingItemIndex,
    promptSnackBar,
  ]);

  /**
   * Batch update publish prior to serving time for all menus in the loop
   */
  const handleBatchUpdatePublishPriorToServingTime = useCallback(() => {
    if (
      !editingMenuLoop ||
      batchUpdatingPublishPriorToServingTime === null ||
      batchUpdatingPublishPriorToServingTime === undefined ||
      !Number.isInteger(batchUpdatingPublishPriorToServingTime) ||
      batchUpdatingPublishPriorToServingTime <= 0
    ) {
      return;
    }

    // Convert hours to ISO 8601 duration format (e.g., "PT2H" for 2 hours)
    const publishPriorToServingTimeDuration = dayjs
      .duration(batchUpdatingPublishPriorToServingTime, "hours")
      .toISOString();

    // Get all menus from menu_links filtered by serving time
    const menus = editingMenuLoop.menu_links
      // .filter((menuLink) => menuLink.serving_time === chosenServingTime)
      .map((menuLink) => menuLink.menu);

    // Convert menus to MenuUpdateDeepInBatch format
    const menusUpdateDeepInBatch = getMenusUpdateDeepInBatchFromMenus(menus);

    // Update publish_prior_to_serving_time in the batch format
    const updatedMenusUpdateDeepInBatch = menusUpdateDeepInBatch.map(
      (menuUpdate) => ({
        ...menuUpdate,
        publish_prior_to_serving_time: publishPriorToServingTimeDuration,
      }),
    );

    // Dispatch batch update
    setIsUpdatingPublishPriorToServingTime(true);
    dispatch(
      menuBatchOperationActions.batchUpdateMenus(updatedMenusUpdateDeepInBatch),
    )
      .unwrap()
      .then((updatedMenus) => {
        setIsUpdatingPublishPriorToServingTime(false);
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Successfully update ${updatedMenus.length} menu(s) in loop`,
          severity: "success",
          autoHeightDuration: 5000,
        });
      })
      .catch((error) => {
        setIsUpdatingPublishPriorToServingTime(false);
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: typeof error === "string" ? error : "Failed to update menus",
          severity: "error",
        });
      });
  }, [
    editingMenuLoop,
    batchUpdatingPublishPriorToServingTime,
    dispatch,
    chosenServingTime,
    promptSnackBar,
  ]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={4} xl={4}>
        <Box display="flex" justifyContent="center">
          <Box sx={{ width: "336px" }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <SideLabel sizepx={40} />
                  {Array.from(["S", "M", "T", "W", "T", "F", "S"]).map(
                    (weekDaySymbol) => (
                      <SideLabel key={weekDaySymbol} sizepx={40}>
                        <Typography variant="overline">
                          {weekDaySymbol}
                        </Typography>
                      </SideLabel>
                    ),
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <DayGridCanvas
                  style={{
                    height: `${
                      displayWeekNumber * DAY_ROW_PITCH_PX -
                      (DAY_ROW_PITCH_PX - DAY_CELL_SIZE_PX)
                    }px`,
                  }}
                >
                  {Array.from(
                    { length: displayWeekNumber },
                    (_, index) => index,
                  ).map((weekInLoopIdx) => (
                    <WeekLabelCell
                      key={weekInLoopIdx}
                      sizepx={DAY_CELL_SIZE_PX}
                      style={{ top: `${weekInLoopIdx * DAY_ROW_PITCH_PX}px` }}
                    >
                      <Typography variant="overline">
                        W{weekInLoopIdx + 1}
                      </Typography>
                    </WeekLabelCell>
                  ))}
                  {Array.from(
                    { length: daysPerCycle ?? 0 },
                    (_, index) => index + 1,
                  ).map(buildDayIndexCell)}
                </DayGridCanvas>
              </Grid>
              <Grid item xs={12}>
                <SideLabel sizepx={45} />
                <SideLabel sizepx={35} />
                <ALaCartePillButton
                  text="à la carte"
                  onClick={() => {
                    setFocusDayIdx(0);
                  }}
                  positive={
                    focusDayIdx === 0 ||
                    (editingMenuLoop?.menu_links
                      .filter(
                        (menuInLoop) => menuInLoop.looping_day_index === 0,
                      )
                      .map((menuInLoop) => menuInLoop.menu)?.length ?? 0) > 0
                  }
                  ok={
                    focusDayIdx !== 0 &&
                    (editingMenuLoop?.menu_links
                      .filter(
                        (menuInLoop) => menuInLoop.looping_day_index === 0,
                      )
                      .map((menuInLoop) => menuInLoop.menu)?.length ?? 0) > 0
                  }
                />
              </Grid>
              {focusDayIdx > 0 && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    mt={2}
                  >
                    {focusDayIdx === startDayIndex ? (
                      <StartDayLabel variant="body2">
                        <StartDayLabelDot />
                        Start day
                      </StartDayLabel>
                    ) : (
                      <CommonButton
                        text="Set start day"
                        positive={!isSavingMenuLoop}
                        onClick={handleSetAsStartDay}
                        disabled={isSavingMenuLoop}
                      />
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} lg={8} xl={8}>
        <Stack direction="column-reverse" spacing={1}>
          <Box display="flex" justifyContent="center">
            <CommonButton
              text="Create New Menu Template"
              positive={
                !!editingMenuLoop?.name &&
                displayMenus.length === 0 &&
                !isSavingMenuLoop
              }
              onClick={handleOpenCreateMenuDialog}
              disabled={
                !editingMenuLoop?.name ||
                displayMenus.length > 0 ||
                isSavingMenuLoop
              }
            />
          </Box>
          <Box display="flex" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              Or
            </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <SpacedAutocompleteInput
              loading={menuOptionsIsLoading}
              options={Object.keys(menuOptionToIdMap)}
              label="Select menu to attach"
              onChange={handleSelectedMenuChange}
            />
            <CommonButton
              text="Add"
              positive={displayMenus.length === 0 && !isSavingMenuLoop}
              onClick={handleAddMenuClick}
              disabled={displayMenus.length > 0 || isSavingMenuLoop}
            />
          </Box>
          <Box display="flex" justifyContent="center">
            <MealTypeSwitchButtonGroup>
              {MEAL_SERVING_TIME_OPTIONS.map(({ label, servingTime }) => (
                <MealTypeSwitchButton
                  key={servingTime}
                  text={label}
                  positive={chosenServingTime === servingTime}
                  onClick={() => {
                    setChosenServingTime(servingTime);
                  }}
                />
              ))}
            </MealTypeSwitchButtonGroup>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
              <Tooltip
                title={`Be careful! You are going to change some menus currently not displayed! 
                  And, this action can not undo. 
                  Don't use if you don't know what menus will be changed.`}
              >
                <WarningIcon
                  sx={{ fontSize: "1.5rem", color: "warning.main", mr: 0.5 }}
                />
              </Tooltip>
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                Set {!batchUpdatingItemIndex ? `` : `the`}
              </Box>
              <SpacedDropDownSelect
                size="small"
                label="Index"
                options={Object.fromEntries(
                  Object.values(MenuItemIndexOptions).map((value, index) => [
                    value,
                    index,
                  ]),
                )}
                value={
                  batchUpdatingItemIndex === null ? 0 : batchUpdatingItemIndex
                }
                onChange={(event) => {
                  const selectedIndex = event.target.value as number;
                  if (selectedIndex === 0) {
                    setBatchUpdatingItemIndex(null);
                  } else {
                    setBatchUpdatingItemIndex(selectedIndex);
                  }
                }}
              />
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                item{!batchUpdatingItemIndex ? `s` : ``} in all{" "}
                {getMealLabelByServingTime(chosenServingTime).toLowerCase()}
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap">
              <SpacedDropDownSelect
                size="small"
                label="Section"
                options={Object.fromEntries(
                  getMenuSectionNameOptions().map((value) => [value, value]),
                )}
                value={batchUpdatingSectionName}
                onChange={handleBatchUpdateSectionNameChange}
                sx={{
                  maxWidth: "fit-content",
                  width: "auto",
                  minWidth: "100px",
                }}
              />
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                to
              </Box>
              <SpacedTextInput
                size="small"
                type="number"
                label="Production"
                value={batchUpdatingProductionNumber ?? ""}
                onChange={handleBatchUpdateProductionNumberChange}
                sx={{
                  maxWidth: "120px",
                  width: "auto",
                }}
              />
              <Box display="flex" alignItems="center">
                <CommonButton
                  text="Update"
                  positive={
                    !(
                      isUpdatingProductionNumbers ||
                      !batchUpdatingSectionName ||
                      batchUpdatingProductionNumber === null ||
                      batchUpdatingProductionNumber === undefined ||
                      !Number.isInteger(batchUpdatingProductionNumber) ||
                      batchUpdatingProductionNumber < 0
                    )
                  }
                  onClick={handleBatchUpdateProductionNumbers}
                  disabled={
                    isUpdatingProductionNumbers ||
                    !batchUpdatingSectionName ||
                    batchUpdatingProductionNumber === null ||
                    batchUpdatingProductionNumber === undefined ||
                    !Number.isInteger(batchUpdatingProductionNumber) ||
                    batchUpdatingProductionNumber < 0
                  }
                />
                {isUpdatingProductionNumbers && (
                  <ColoredCircularProgress
                    customColor={css`rgb(200, 200, 200)`}
                    size={20}
                  />
                )}
              </Box>
            </Box>
          </Box>
          {/* <Box*/}
          {/*  display="flex"*/}
          {/*  justifyContent="center"*/}
          {/*  alignItems="center"*/}
          {/*  gap={1}*/}
          {/*  flexWrap="wrap"*/}
          {/* >*/}
          {/*  <Box display="flex" alignItems="center" gap={1} flexShrink={0}>*/}
          {/*    <Tooltip*/}
          {/*      title={`Be careful! You are going to change some menus currently not displayed! */}
          {/*        And, this action can not undo. Sometimes, this action can lead to skip publishing */}
          {/*        or double publishing.*/}
          {/*        Don't use if you don't know what menus will be changed.`}*/}
          {/*    >*/}
          {/*      <WarningIcon*/}
          {/*        sx={{ fontSize: "1.5rem", color: "warning.main", mr: 0.5 }}*/}
          {/*      />*/}
          {/*    </Tooltip>*/}
          {/*    <Box component="span" sx={{ whiteSpace: "nowrap" }}>*/}
          {/*      Publish all{" "}*/}
          {/*      /!* {chosenServingTime === "12:00:00" ? "lunch" : "dinner"} *!/*/}
          {/*      menus in loop*/}
          {/*    </Box>*/}
          {/*  </Box>*/}
          {/*  <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap">*/}
          {/*    <SpacedTextInput*/}
          {/*      size="small"*/}
          {/*      type="number"*/}
          {/*      value={batchUpdatingPublishPriorToServingTime ?? ""}*/}
          {/*      onChange={handleBatchUpdatePublishPriorToServingTimeChange}*/}
          {/*      InputProps={{*/}
          {/*        endAdornment: (*/}
          {/*          <InputAdornment position="end">hr.</InputAdornment>*/}
          {/*        ),*/}
          {/*      }}*/}
          {/*      sx={{*/}
          {/*        maxWidth: "120px",*/}
          {/*        width: "auto",*/}
          {/*      }}*/}
          {/*    />*/}
          {/*    <Box component="span" sx={{ whiteSpace: "nowrap" }}>*/}
          {/*      before serving time*/}
          {/*    </Box>*/}
          {/*    <Box display="flex" alignItems="center">*/}
          {/*      <CommonButton*/}
          {/*        text="Update"*/}
          {/*        positive={*/}
          {/*          !(*/}
          {/*            isUpdatingPublishPriorToServingTime ||*/}
          {/*            batchUpdatingPublishPriorToServingTime === null ||*/}
          {/*            batchUpdatingPublishPriorToServingTime === undefined ||*/}
          {/*            !Number.isInteger(*/}
          {/*              batchUpdatingPublishPriorToServingTime,*/}
          {/*            ) ||*/}
          {/*            batchUpdatingPublishPriorToServingTime <= 0*/}
          {/*          )*/}
          {/*        }*/}
          {/*        onClick={handleBatchUpdatePublishPriorToServingTime}*/}
          {/*        disabled={*/}
          {/*          isUpdatingPublishPriorToServingTime ||*/}
          {/*          batchUpdatingPublishPriorToServingTime === null ||*/}
          {/*          batchUpdatingPublishPriorToServingTime === undefined ||*/}
          {/*          !Number.isInteger(batchUpdatingPublishPriorToServingTime) ||*/}
          {/*          batchUpdatingPublishPriorToServingTime <= 0*/}
          {/*        }*/}
          {/*      />*/}
          {/*      {isUpdatingPublishPriorToServingTime && (*/}
          {/*        <ColoredCircularProgress*/}
          {/*          customColor={css`rgb(200, 200, 200)`}*/}
          {/*          size={20}*/}
          {/*        />*/}
          {/*      )}*/}
          {/*    </Box>*/}
          {/*  </Box>*/}
          {/* </Box>*/}
        </Stack>
      </Grid>
      <Grid item xs={12} container>
        {displayMenus.map((menu) => (
          <Grid
            key={`${editingMenuLoop.id}-${focusDayIdx}-${chosenServingTime}-${menu.id}`}
            item
            lg={4}
            md={6}
            xs={12}
          >
            {menuOptionsIsLoading ? (
              <ColoredCircularProgress customColor={css`rgb(120, 203, 255)`} />
            ) : (
              <>
                <MenuInLoopActionBox>
                  <CircleIconButton
                    positive
                    danger
                    sizepx={36}
                    disabled={isSavingMenuLoop}
                    onClick={handleRemoveMenuClick(
                      focusDayIdx,
                      menu.id,
                      chosenServingTime,
                    )}
                  >
                    <DeleteIcon />
                  </CircleIconButton>
                </MenuInLoopActionBox>
                <MenuViewBox
                  menu={{
                    // Fall back to the loop link's own menu record when the
                    // menu options fetch failed or didn't include this menu,
                    // so a missing entry degrades instead of crashing.
                    ...(idToMenu[menu.id] ?? menu),
                    serving_datetime: `2025-03-18T${chosenServingTime}`,
                  }}
                  onEdit={handleOpenEditMenuDialog}
                />
              </>
            )}
          </Grid>
        ))}
      </Grid>
      <CreateMenuInLoopDialog
        open={isCreateMenuDialogOpen}
        onClose={handleCloseCreateMenuDialog}
        initialName={generatedMenuName}
        editingMenuId={editingMenuId}
        onSaved={handleMenuSavedInLoop}
      />
    </Grid>
  );
};

export default MenusInLoopEditSection;
